import { z } from 'zod';
import { command, get, post } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';
import { depositSchema } from '../schemas/domain';

export const depositInput = z.object({
  amount_usd: z.coerce.number().positive('Amount must be greater than zero'),
  method: z.enum(['manual', 'gateway', 'blockchain']).default('blockchain'),
  tx_hash: z.string().optional(),
  chain: z.string().optional(),
});
export type DepositInput = z.infer<typeof depositInput>;

/**
 * `status` and `method` stay as plain strings (inherited from ListQuery)
 * because these queries are built from URL search params, which are untyped by
 * definition. The server validates the values; narrowing them here would only
 * force casts at every call site.
 */
export type DepositQuery = ListQuery;

export const depositsApi = {
  /** Company receiving address + QR for the manual/on-chain flow. */
  paymentDetails: () =>
    get(
      '/deposits/payment-details',
      resource(
        z.object({
          address: z.string(),
          chain: z.string(),
          qr_url: z.string().nullable(),
          lds_rate_usd: z.string(),
        }),
      ),
    ),

  create: (input: DepositInput | FormData) => post('/deposits', resource(depositSchema), input),

  list: (query: DepositQuery = {}) => get('/deposits', paginated(depositSchema), query),

  /* admin */
  adminList: (status: 'pending' | 'approved' | 'rejected' | 'all', query: DepositQuery = {}) =>
    get(`/admin/deposits/${status}`, paginated(depositSchema), query),

  /** DApp / on-chain deposits detected by the chain watcher. */
  adminBlockchain: (query: DepositQuery = {}) =>
    get('/admin/deposits/blockchain', paginated(depositSchema), query),

  approve: (id: string, note?: string) => command(`/admin/deposits/${id}/approve`, { note }),
  reject: (id: string, note: string) => command(`/admin/deposits/${id}/reject`, { note }),
};
